import React from 'react';
import styled, { useTheme } from 'styled-components';
import KindredLogo from '../../Common/Logos/KindredLogo';
import images from '../../Theming/images';
import { themeColor } from '../../Theming';
import { useTranslation } from 'react-i18next';
import env from '../../env';
import { usePageViewTracking, useQuery } from '../../Common/Hooks';
import {
  ANALYTICS_ACTION_LABELS,
  ANALYTICS_PAGE_NAMES,
  PartnerNames,
  trackClickedActionEvent,
} from '../../Common/Analytics/utils';

const Container = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background-color: white;
`;

const HyperisePlaceholderContainer = styled.div`
  width: 100%;
  height: fit-content;
`;

const MainContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  box-sizing: border-box;
  clip-path: circle(85% at 15% 50%);
  background-color: #f5f5f5;
`;

const FeatureSection = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 2rem;
  max-width: 500px;
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  margin-right: 16px;
  margin-top: -5px;
  display: flex;
  align-items: center;
  justify-content: center;

  & > img {
    height: 50px;
  }
`;

const KindredLogoContainer = styled.div`
  transform: scale(2);
  margin-bottom: 100px;
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: ${themeColor('darkBlue')};
  line-height: 1.2;
  margin: 0 0 35px 0;
`;

const FeatureDescription = styled.p`
  font-size: 17px;
  color: ${themeColor('darkBlue')};
  line-height: 1.6;
  margin: 0;
`;

const Button = styled.button`
  background-color: ${themeColor('successGreen')};
  color: white;
  border: none;
  padding: 1.2rem 3rem;
  font-size: 1.4rem;
  font-weight: 600;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 3rem;
  transition: all 0.3s ease;
  width: 336px;
  height: 61px;
`;

const DesktopExtensionLanding: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const query = useQuery();
  const utm_campaign = query.get('utm_campaign') as PartnerNames;
  const partnerName = utm_campaign ?? 'Kindred';
  usePageViewTracking(
    ANALYTICS_PAGE_NAMES.desktop_activation_landing_page,
    partnerName
  );

  const onCTAClicked = () => {
    trackClickedActionEvent(
      query,
      partnerName,
      ANALYTICS_ACTION_LABELS.desktop_extension_install,
      ANALYTICS_PAGE_NAMES.desktop_activation_landing_page
    );
    window.location.href = env.chrome_extension_store_url;
  };

  return (
    <Container id="mainContainer">
      <MainContent>
        <HyperisePlaceholderContainer id="beforeLogo">
          <p></p>
        </HyperisePlaceholderContainer>
        <KindredLogoContainer id="kindredLogoContainer">
          <KindredLogo color={theme.colors.darkBlue} />
        </KindredLogoContainer>
        <HyperisePlaceholderContainer id="afterLogo">
          <p></p>
        </HyperisePlaceholderContainer>

        <FeatureSection>
          <FeatureIcon id="section1Icon">
            <img src={images.shoppingBag} alt="Shopping Bag Icon" />
          </FeatureIcon>
          <FeatureContent>
            <FeatureTitle id="section1Title">
              {t('extension.desktopActivation.neverMissDiscounts')}
            </FeatureTitle>
            <FeatureDescription id="section1Description">
              {t('extension.desktopActivation.weFindDeals')}
            </FeatureDescription>
          </FeatureContent>
        </FeatureSection>

        <FeatureSection>
          <FeatureIcon id="section2Icon">
            <img src={images.tree} alt="Tree Icon" />
          </FeatureIcon>
          <FeatureContent>
            <FeatureTitle id="section2Title">
              {t('extension.desktopActivation.shopAndSave')}
            </FeatureTitle>
            <FeatureDescription id="section2Description">
              {t('extension.desktopActivation.contributeToTrees')}
            </FeatureDescription>
          </FeatureContent>
        </FeatureSection>

        <Button id="letsGoButton" onClick={onCTAClicked}>
          {t('extension.desktopActivation.letsGo')}
        </Button>
      </MainContent>
    </Container>
  );
};

export default DesktopExtensionLanding;
